/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback, useEffect, useState } from "react";
import { type MatrixClient } from "matrix-js-sdk";

export interface SessionDevice {
  deviceId: string;
  displayName?: string;
  lastSeenIp?: string;
  lastSeenTs?: number;
  isCurrent: boolean;
}

/**
 * Lists and revokes devices logged into the local Matrix account. Pure
 * Matrix-JS-SDK calls — no third party, no paid API.
 */
export function useSessionManager(
  client: MatrixClient | null,
): {
  sessions: SessionDevice[];
  loading: boolean;
  refresh: () => Promise<void>;
  revoke: (deviceId: string) => Promise<void>;
} {
  const [sessions, setSessions] = useState<SessionDevice[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const { devices } = await client.getDevices();
      const current = client.getDeviceId();
      setSessions(
        (devices ?? []).map((d) => ({
          deviceId: d.device_id,
          displayName: d.display_name ?? undefined,
          lastSeenIp: d.last_seen_ip,
          lastSeenTs: d.last_seen_ts,
          isCurrent: d.device_id === current,
        })),
      );
    } finally {
      setLoading(false);
    }
  }, [client]);

  const revoke = useCallback(
    async (deviceId: string) => {
      if (!client) return;
      // In production this requires an auth flow (password/SSS); we trigger it
      // through the SDK which surfaces the necessary UI in the host app.
      await client.deleteDevice(deviceId, (await client.getCrypto()) as never);
      await refresh();
    },
    [client, refresh],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { sessions, loading, refresh, revoke };
}
